import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import {
  getSerialCodes,
  GetSerialCodesRequest,
  SerialCodeData,
} from '@/application/requests/serialCode';

export interface SerialCodeState {
  serialCodes: SerialCodeData[];
  loading: boolean;
  error: string | null;
  currentPage: number;
  totalPages: number;
  totalItems: number;
  pageSize: number;
  hasNext: boolean;
  hasPrevious: boolean;
}

const initialState: SerialCodeState = {
  serialCodes: [],
  loading: false,
  error: null,
  currentPage: 1,
  totalPages: 1,
  totalItems: 0,
  pageSize: 10,
  hasNext: false,
  hasPrevious: false,
};

export const fetchSerialCodes = createAsyncThunk(
  'serialCode/fetchSerialCodes',
  async (params: GetSerialCodesRequest, { rejectWithValue }) => {
    try {
      const response = await getSerialCodes(params);

      if (!response.success) {
        return rejectWithValue(
          response.message || 'Failed to fetch serial codes'
        );
      }

      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch serial codes');
    }
  }
);

const serialCodeSlice = createSlice({
  name: 'serialCode',
  initialState,
  reducers: {
    setCurrentPage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload;
    },
    setPageSize: (state, action: PayloadAction<number>) => {
      state.pageSize = action.payload;
      state.currentPage = 1; // Reset to first page when changing page size
    },
    clearError: (state) => {
      state.error = null;
    },
    resetSerialCodes: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSerialCodes.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSerialCodes.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload) {
          state.serialCodes = action.payload.data;
          state.totalPages = action.payload.total_pages;
          state.totalItems = action.payload.total;
          state.currentPage = action.payload.page;
          state.pageSize = action.payload.limit;
          state.hasNext = action.payload.has_next;
          state.hasPrevious = action.payload.has_previous;
        }
      })
      .addCase(fetchSerialCodes.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { setCurrentPage, setPageSize, clearError, resetSerialCodes } =
  serialCodeSlice.actions;

export default serialCodeSlice.reducer;
